import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import AwardsNavbar from "@/components/sections/AwardsNavbar";
import { Wordmark } from "@/components/Brand";
import NominationModal from "@/components/sections/NominationModal";
import {
  Rocket,
  Lightbulb,
  Building2,
  TrendingUp,
  Leaf,
  Users,
  Megaphone,
  Star,
  ChevronLeft,
  ChevronRight,
  User,
  BadgeCheck,
  CalendarDays,
  Trophy,
  ArrowRight,
  Sprout,
  GraduationCap,
  Factory,
  ShoppingBag,
  Home,
  Zap,
  Crown,
  Briefcase,
  Compass,
  Cpu,
  Globe,
  Award as AwardIcon,
  Sparkles,
  Check,
  ClipboardList,
  FileText,
  UploadCloud,
  CreditCard,
  Gavel,
  PartyPopper,
} from "lucide-react";

/**
 * ---------------------------------------------------------------------------
 * HERO BACKGROUND IMAGE
 * ---------------------------------------------------------------------------
 * Drop your image into: frontend/public/images/awards-hero-bg.jpg
 * (or update HERO_BG_IMAGE below to match wherever you place it / import it).
 * The hero is wrapped in its own rounded "card" — the image sits behind a
 * cream gradient scrim so the headline stays readable on any photo.
 *
 * The image only renders at the lg breakpoint (1024px+), which is the same
 * breakpoint where the layout switches to two columns and reserves empty
 * space on the right for it. Below lg, the layout is a single column and
 * showing the image there would overlap the text — so it's hidden entirely.
 * ---------------------------------------------------------------------------
 */
const HERO_BG_IMAGE = "/Event/award_hero.png";

const awardsHeroFontStyles = `

  .aw-hero-title {
    font-family: 'Playfair Display', Georgia, serif !important;
    font-weight: 500;
    line-height: 1.05;
    letter-spacing: -2px;
    text-transform: uppercase;
    margin: 0;
    display: block;
  }
  .aw-hero-title .line1 {
    font-size: clamp(2.6rem, 5.2vw, 4.2rem);
    display: block;
  }
  .aw-hero-title .line2 {
    font-size: clamp(2.2rem, 4.4vw, 3.6rem);
    display: block;
    letter-spacing: -1.5px;
  }
  .aw-hero-title .line3 {
    font-size: clamp(2.6rem, 5.2vw, 4.2rem);
    display: block;
     margin-top: -0.08em;
  }

  .aw-hero-bg-image {
    background-position: right -45% top 60%;
  }
  @media (min-width: 1024px) {
    .aw-hero-bg-image {
      background-position: right -10% top 60%;
    }
  }
`;

/**
 * ---------------------------------------------------------------------------
 * AWARD CATEGORIES — full official list of 20 categories, grouped into the
 * 4 official groups (Startup, MSME, Leadership, Innovation). Rendered as a
 * tabbed grid below so all 20 are reachable without overwhelming the page.
 * ---------------------------------------------------------------------------
 */
const CATEGORY_GROUPS = [
  {
    key: "startup",
    label: "Startup",
    accent: "#F4801F",
    items: [
      {
        icon: Rocket,
        title: "Startup of the Year",
        desc: "Recognizing innovative startups with outstanding growth potential.",
      },
      {
        icon: Sprout,
        title: "Emerging Startup",
        desc: "Celebrating early-stage startups showing exceptional promise.",
      },
      {
        icon: Users,
        title: "Women-led Startup",
        desc: "Honoring startups founded and led by women entrepreneurs.",
      },
      {
        icon: GraduationCap,
        title: "Student Entrepreneur Award",
        desc: "Recognizing students building ventures alongside their studies.",
      },
      {
        icon: Megaphone,
        title: "Social Impact Startup",
        desc: "Celebrating startups solving pressing social challenges.",
      },
    ],
  },
  {
    key: "msme",
    label: "MSME",
    accent: "#1E8A3C",
    items: [
      {
        icon: Building2,
        title: "MSME of the Year",
        desc: "Recognizing MSMEs for outstanding performance and impact.",
      },
      {
        icon: Factory,
        title: "Manufacturing Excellence",
        desc: "Honoring manufacturers driving quality and efficiency.",
      },
      {
        icon: ShoppingBag,
        title: "Retail Excellence",
        desc: "Celebrating retail businesses delivering standout customer experiences.",
      },
      {
        icon: Home,
        title: "Family Business Excellence",
        desc: "Recognizing family-run businesses built on legacy and trust.",
      },
      {
        icon: TrendingUp,
        title: "Fastest Growing Business",
        desc: "Honoring businesses with exceptional year-on-year growth.",
      },
    ],
  },
  {
    key: "leadership",
    label: "Leadership",
    accent: "#0B2447",
    items: [
      {
        icon: Zap,
        title: "Young Entrepreneur of the Year",
        desc: "Celebrating young leaders building bold new ventures.",
      },
      {
        icon: Crown,
        title: "Woman Entrepreneur of the Year",
        desc: "Celebrating women leaders who are inspiring change and creating impact.",
      },
      {
        icon: Briefcase,
        title: "Business Leader of the Year",
        desc: "Recognizing leadership excellence and organizational impact.",
      },
      {
        icon: Compass,
        title: "Visionary CEO Award",
        desc: "Honoring CEOs charting bold, future-ready direction.",
      },
      {
        icon: Star,
        title: "Lifetime Achievement Award",
        desc: "Honoring extraordinary contributions and a legacy of excellence.",
      },
    ],
  },
  {
    key: "innovation",
    label: "Innovation",
    accent: "#F4801F",
    items: [
      {
        icon: Cpu,
        title: "Digital Transformation Award",
        desc: "Recognizing businesses reshaping operations through technology.",
      },
      {
        icon: Lightbulb,
        title: "Innovation Excellence Award",
        desc: "Honoring individuals who are driving innovation and disruption.",
      },
      {
        icon: Globe,
        title: "Export Excellence Award",
        desc: "Celebrating businesses expanding India's presence in global markets.",
      },
      {
        icon: Leaf,
        title: "Sustainability Champion",
        desc: "Honoring leaders building sustainable and responsible businesses.",
      },
      {
        icon: AwardIcon,
        title: "Brand Excellence Award",
        desc: "Recognizing brands that have built exceptional trust and recall.",
      },
    ],
  },
];

// Flattened list — used wherever the full category set is needed elsewhere
// on the page (e.g. passed into the NominationModal's category picker).
const CATEGORIES = CATEGORY_GROUPS.flatMap((group) =>
  group.items.map((item) => ({ ...item, group: group.label }))
);

const STATS = [
  { icon: User, label: "Open for Individuals\n& Organizations" },
  { icon: BadgeCheck, label: "Multiple Award\nCategories" },
  { icon: CalendarDays, label: "Nomination Deadline\n10th August, 2026" },
  { icon: Trophy, label: "Winners Felicitated at\nFreedom Conclave 2026" },
];

/**
 * ---------------------------------------------------------------------------
 * NOMINATION PACKAGES — pricing tiers for the nomination flow.
 * ---------------------------------------------------------------------------
 */
const NOMINATION_PACKAGES = [
  {
    key: "basic",
    name: "Basic",
    price: "₹4,999",
    accent: "#0B2447",
    highlighted: false,
    features: [
      "Official Nomination",
      "Jury Evaluation",
      "Digital Certificate",
      "Official Nominee Badge",
    ],
  },
  {
    key: "professional",
    name: "Professional",
    price: "₹9,999",
    accent: "#F4801F",
    highlighted: true,
    featuresIntro: "Everything in Basic, plus:",
    features: [
      "Two Event Passes",
      "Social Media Feature",
      "Website Listing",
      "Press Mention",
    ],
  },
  {
    key: "premium",
    name: "Premium",
    price: "₹24,999",
    accent: "#1E8A3C",
    highlighted: false,
    featuresIntro: "Everything in Professional, plus:",
    features: [
      "Stage Recognition",
      "Premium Seating",
      "Business Interview",
      "Professional Brand Shoot",
      "Networking Dinner",
      "Magazine Feature",
    ],
  },
];

/**
 * ---------------------------------------------------------------------------
 * NOMINATION PROCESS — step flow shown on the nomination page/section.
 * Each step now carries a description, an estimated time/duration, and an
 * accent color (cycled orange / royal-blue / green to match the IBH palette)
 * so the redesigned cards can show icon, copy and a time chip like the
 * reference design.
 * ---------------------------------------------------------------------------
 */
const NOMINATION_PROCESS = [
  {
    icon: Trophy,
    title: "Select Award Category",
    desc: "Choose the category that best represents your business.",
    accent: "#F4801F",
  },
  {
    icon: User,
    title: "Fill Business Profile",
    desc: "Share your company details and tell us your story.",
    accent: "#002A9B",
  },
  {
    icon: ClipboardList,
    title: "Submit Achievements",
    desc: "Highlight your key achievements and impact.",
    accent: "#1E8A3C",
  },
  {
    icon: UploadCloud,
    title: "Upload Business Proof",
    desc: "Upload relevant documents and supporting proof.",
    accent: "#F4801F",
  },
  {
    icon: CreditCard,
    title: "Pay Nomination Fee",
    desc: "Complete the nomination fee securely online.",
    accent: "#002A9B",
  },
  {
    icon: Gavel,
    title: "Jury Evaluation",
    desc: "Our expert jury evaluates all nominations.",
    accent: "#1E8A3C",
  },
  {
    icon: Megaphone,
    title: "Finalists Announced",
    desc: "Top nominations are shortlisted as finalists.",
    accent: "#002A9B",
  },
  {
    icon: PartyPopper,
    title: "Award Ceremony",
    desc: "Winners are honored at the grand award ceremony.",
    accent: "#F4801F",
  },
];

// ── FOOTER DATA (kept separate/local to this page — not shared with MegaEvent) ──
const awardsLinkGroups = [
  {
    title: "Quick Links",
    links: [
      { label: "About", href: "#about" },
      { label: "Networking", href: "#networking" },
      { label: "Speakers", href: "#speakers" },
      { label: "Sponsors", href: "#sponsors" },
    ],
  },
  {
    title: "Sponsorships",
    links: [
      { label: "Event Brochure", href: "/Event/Freedom Conclave 2026_Event Brochure.pdf" },
      { label: "Sponsorship Opportunities", href: "/Event/Freedom Conclave 2026_Sponsorship Slabs.pdf" },
      { label: "Partner with Us", href: "mailto:connect@myibh.in" },
    ],
  },
];

const awardsSocials = [
  { icon: "fa-brands fa-linkedin-in", url: "https://www.linkedin.com/company/india-business-helpline/", hoverBg: "#0A66C2" },
  { icon: "fa-brands fa-x-twitter", url: "https://x.com/ibh_info", hoverBg: "#1a1a1a" },
  { icon: "fa-brands fa-instagram", url: "https://www.instagram.com/indiabusinesshelpline/", hoverBg: "#E1306C" },
  { icon: "fa-brands fa-youtube", url: "https://www.youtube.com/@indiabusinesshelpline", hoverBg: "#FF0000" },
  { icon: "fa-brands fa-whatsapp", url: "https://wa.me/918274964884", hoverBg: "#25D366" },
  { icon: "fa-brands fa-facebook-f", url: "https://www.facebook.com/profile.php?id=61566777813226", hoverBg: "#1877F2" },
];

// ── Local copy of SocialIcon — intentionally duplicated (not imported) so
// this page's footer stays fully independent from MegaEvent's footer. ──
const AwardsFooterSocialIcon = ({ icon, url, hoverBg }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        backgroundColor: hovered ? hoverBg : "rgba(255,255,255,0.08)",
        borderColor: hovered ? hoverBg : "rgba(255,255,255,0.10)",
        borderWidth: "1px",
        borderStyle: "solid",
        transition: "background-color 0.2s, border-color 0.2s",
      }}
      className="h-10 w-10 rounded-full flex items-center justify-center"
    >
      <i className={`${icon} text-sm text-white`}></i>
    </a>
  );
};

function Eyebrow({ children }) {
  return (
    <div className="flex items-center justify-center gap-3 text-xs font-semibold tracking-[0.25em] text-[#F4801F] mb-3">
      <span className="h-px w-8 bg-[#F4801F]/60" />
      {children}
      <span className="h-px w-8 bg-[#1E8A3C]/60" />
    </div>
  );
}

// ── Step card used in the redesigned Nomination Process section ──
function ProcessStepCard({ step, index }) {
  const { icon: Icon, title, desc, accent } = step;
  return (
    <div className="relative bg-white rounded-2xl p-6 pt-8 shadow-sm ring-1 ring-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all text-left h-full flex flex-col">
      {/* Numbered badge, overlapping the top-left corner */}
      <span
        className="absolute -top-4 -left-3 h-9 w-9 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-md ring-4 ring-[#FBF7F0]"
        style={{ backgroundColor: accent }}
      >
        {String(index + 1).padStart(2, "0")}
      </span>

      <span
        className="inline-flex h-14 w-14 items-center justify-center rounded-full mb-4"
        style={{ backgroundColor: `${accent}1A` }}
      >
        <Icon size={24} style={{ color: accent }} />
      </span>

      <h3 className="font-semibold text-[#0B2447] mb-1.5 leading-snug">
        {title}
      </h3>
      <p className="text-sm text-slate-500 leading-relaxed flex-1">
        {desc}
      </p>
    </div>
  );
}

// ── Horizontal connector shown between cards in the same row (lg+ only) ──
function RowConnector() {
  return (
    <div
      className="hidden lg:flex absolute top-11 -right-4 xl:-right-5 w-8 xl:w-10 items-center justify-center z-10"
      aria-hidden="true"
    >
      <span className="h-px flex-1 bg-slate-200" />
      <span className="h-2 w-2 rounded-full border-2 border-slate-300 bg-[#FBF7F0] shrink-0" />
      <span className="h-px flex-1 bg-slate-200" />
    </div>
  );
}

export default function Awards() {
  // Controls the NominationModal open/closed state — shared by every
  // "Nominate Now" trigger on this page.
  const [nominationOpen, setNominationOpen] = useState(false);
  // Controls which of the 4 category groups is active in the categories tab bar.
  const [activeGroup, setActiveGroup] = useState(CATEGORY_GROUPS[0].key);

  const currentGroup =
    CATEGORY_GROUPS.find((g) => g.key === activeGroup) ?? CATEGORY_GROUPS[0];

  return (
    <div className="bg-[#FBF7F0] text-slate-900">
      <Helmet>
        <title>Freedom Excellence Awards 2026 | IBH — India Business Helpline</title>
        <meta name="description" content="Freedom Excellence Awards 2026 — honoring exceptional entrepreneurs, startups, leaders and organizations building a self-reliant India. Nominations open." />
      </Helmet>
      <style>{awardsHeroFontStyles}</style>
      <AwardsNavbar />

      {/* ============================================================= */}
      {/* HERO — wrapped in a card, background image sits behind scrim  */}
      {/* ============================================================= */}
      <section className="px-4 sm:px-6 lg:px-10 pt-16 sm:pt-20 pb-4" id="about">
        <div className="relative overflow-hidden rounded-[28px] shadow-xl ring-1 ring-black/5 bg-[#FBF7F0]">
          {/* Background image layer — rendered at md+ (768px), matching the
              breakpoint where the grid below also becomes two columns and
              reserves empty space on the right for it. Below md, this div
              doesn't exist in the DOM, so no overlap and no image on phones. */}
          <div
            className="hidden md:block aw-hero-bg-image absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `linear-gradient(110deg, rgba(251,247,240,0.92) 0%, rgba(251,247,240,0.55) 40%, rgba(251,247,240,0) 65%), url(${HERO_BG_IMAGE})`,
              backgroundSize: "auto 155%",
              backgroundRepeat: "no-repeat",
            }}
          />

          <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center px-6 sm:px-10 lg:px-16 py-16 lg:py-24">
            {/* Copy */}
            <div className="max-w-xl">
              <h1 className="aw-hero-title text-[#0B2447]">
  <span className="line1">
    FREEDOM
    <sup
      style={{
        fontSize: "0.2em",
        fontWeight: 350,
        fontFamily: "'Poppins', sans-serif",
        letterSpacing: "0.02em",
        color: "#0B2447",
        verticalAlign: "super",
        position: "relative",
        top: "-1.05em",
        marginLeft: "1px",
      }}
    >
      TM
    </sup>
  </span>
  <span className="line2">EXCELLENCE</span>
  <span className="line3 text-[#F4801F]">
    AWARDS{" "}
    <span style={{ fontFamily: "'Times New Roman', Times, serif" }}>
      2026
    </span>
  </span>
</h1>
              <p className="text-lg text-slate-800 font-medium mb-4 mt-1">
                Celebrating Vision. Recognizing Impact. Honouring the
                Builders of New India.
              </p>

              <div className="flex items-center gap-3 my-6">
                <span className="h-[3px] w-10 bg-[#F4801F] rounded-full" />
                <span className="h-2 w-2 rounded-full bg-[#0B2447]/40" />
                <span className="h-[3px] w-10 bg-[#1E8A3C] rounded-full" />
              </div>

              <p className="text-slate-600 leading-relaxed mb-8">
                The Freedom Excellence Awards honor exceptional entrepreneurs,
                startups, leaders and organizations who are driving
                innovation, creating impact and building a stronger,
                self-reliant India.
              </p>

              <div className="flex flex-col items-center gap-1.5" style={{ width: "fit-content" }}>
                <button onClick={() => setNominationOpen(true)} className="inline-flex items-center gap-2 bg-[#0B2447] text-white font-semibold px-6 py-3 rounded-full hover:bg-[#123a6b] transition-colors" aria-label="Open nomination form">
  Nominate Now
  <ArrowRight size={16} />
</button>
                <span className="text-sm font-semibold tracking-wide text-[#F4801F]">
                  Nominations are open
                </span>
              </div>
            </div>

            {/* Right side — only reserved as empty space at md+, so image shows through there */}
            <div className="hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* NOMINATIONS BANNER                                             */}
      {/* ============================================================= */}
      <section className="bg-[#0B2447] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <Eyebrow>BE PART OF THE RECOGNITION</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl mb-4">
            Nominations Are Open
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-10">
            Do you know an inspiring entrepreneur, startup or leader making a
            real difference? Nominate them for the Freedom Excellence Awards
            2026.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
            {STATS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-3">
                <span className="h-14 w-14 rounded-full border border-white/25 flex items-center justify-center">
                  <Icon size={22} className="text-[#F4801F]" />
                </span>
                <p className="text-sm text-white/80 whitespace-pre-line leading-snug">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* CATEGORIES — all 20 official categories, grouped into 4 tabs   */}
      {/* (Startup / MSME / Leadership / Innovation), 5 per group.       */}
      {/* ============================================================= */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center" id="categories">
        <Eyebrow>AWARD CATEGORIES</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#0B2447] mb-3">
          Categories of Awards
        </h2>
        <p className="text-slate-500 max-w-xl mx-auto mb-10">
          20 categories across 4 groups — explore each to see who it's for.
        </p>

        {/* Tab bar */}
        <div
          role="tablist"
          aria-label="Award category groups"
          className="inline-flex flex-wrap justify-center gap-2 bg-white rounded-full p-1.5 shadow-sm ring-1 ring-slate-100 mb-12"
        >
          {CATEGORY_GROUPS.map((group) => {
            const isActive = group.key === activeGroup;
            return (
              <button
                key={group.key}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveGroup(group.key)}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0B2447] ${
                  isActive
                    ? "text-white"
                    : "text-slate-500 hover:text-[#0B2447]"
                }`}
                style={isActive ? { backgroundColor: group.accent } : undefined}
              >
                {group.label}
              </button>
            );
          })}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 text-left">
          {currentGroup.items.map(({ icon: Icon, title, desc }) => (
            <div
              key={title}
              className="bg-white rounded-2xl p-6 shadow-sm ring-1 ring-slate-100 hover:shadow-md hover:-translate-y-0.5 transition-all"
            >
              <span
                className="inline-flex h-12 w-12 items-center justify-center rounded-full mb-5"
                style={{ backgroundColor: currentGroup.accent }}
              >
                <Icon size={20} className="text-white" />
              </span>
              <h3 className="font-semibold text-[#0B2447] mb-2">{title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================= */}
      {/* NOMINATION PROCESS — redesigned step flow                     */}
      {/* Numbered cards with icon, description and a time estimate,    */}
      {/* connected by simple row connectors on lg+, ending in a CTA    */}
      {/* banner. Grid is 2 cols on sm, 4 cols (2 rows of 4) on lg.     */}
      {/* ============================================================= */}
      <section className="max-w-6xl mx-auto px-6 py-20 text-center">
        <Eyebrow>HOW IT WORKS</Eyebrow>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#0B2447] mb-3">
          Nomination Process
        </h2>
        <p className="text-slate-500 max-w-xl mx-auto mb-14">
          From nomination to the big night — here's what the journey looks like.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10 lg:gap-y-14">
          {NOMINATION_PROCESS.map((step, idx) => {
            // No connector after the last card in each row of 4 (index 3 and 7)
            const isRowEnd = idx % 4 === 3;
            return (
              <div key={step.title} className="relative">
                <ProcessStepCard step={step} index={idx} />
                {!isRowEnd && <RowConnector />}
              </div>
            );
          })}
        </div>

        
      </section>

      {/* ============================================================= */}
      {/* NOMINATION PACKAGES — pricing tiers                            */}
      {/* ============================================================= */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 py-20 text-center" id="nominate">
          <Eyebrow>NOMINATION PACKAGES</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#0B2447] mb-3">
            Choose Your Nomination Package
          </h2>
          <p className="text-slate-500 max-w-xl mx-auto mb-14">
            Pick the package that fits how visible you want your nomination
            to be.
          </p>

          <div className="grid md:grid-cols-3 gap-8 text-left">
            {NOMINATION_PACKAGES.map((pkg) => (
              <div
                key={pkg.key}
                className={`relative rounded-2xl p-8 flex flex-col ${
                  pkg.highlighted
                    ? "bg-[#0B2447] text-white shadow-xl ring-1 ring-[#0B2447] md:-translate-y-3"
                    : "bg-[#FBF7F0] text-slate-900 ring-1 ring-slate-100"
                }`}
              >
                {pkg.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 bg-[#F4801F] text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    <Sparkles size={12} />
                    MOST POPULAR
                  </span>
                )}

                <h3
                  className={`font-serif text-2xl mb-1 ${
                    pkg.highlighted ? "text-white" : "text-[#0B2447]"
                  }`}
                >
                  {pkg.name}
                </h3>
                {/* price intentionally hidden on this page — shown only inside NominationModal  */}

                {pkg.featuresIntro && (
                  <p
                    className={`text-xs font-semibold mb-3 ${
                      pkg.highlighted ? "text-white/60" : "text-slate-400"
                    }`}
                  >
                    {pkg.featuresIntro}
                  </p>
                )}

                <ul className="space-y-3 mb-8 flex-1">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2.5">
                      <span
                        className="mt-0.5 h-5 w-5 rounded-full flex items-center justify-center shrink-0"
                        style={{
                          backgroundColor: pkg.highlighted
                            ? "rgba(255,255,255,0.15)"
                            : `${pkg.accent}1A`,
                        }}
                      >
                        <Check
                          size={12}
                          strokeWidth={3}
                          style={{ color: pkg.highlighted ? "#fff" : pkg.accent }}
                        />
                      </span>
                      <span
                        className={`text-sm leading-snug ${
                          pkg.highlighted ? "text-white/90" : "text-slate-600"
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => setNominationOpen(true)}
                  className={`inline-flex items-center justify-center gap-2 font-semibold px-6 py-3 rounded-full transition-colors ${
                    pkg.highlighted
                      ? "bg-[#F4801F] text-white hover:bg-[#d96e15]"
                      : "bg-[#0B2447] text-white hover:bg-[#123a6b]"
                  }`}
                  aria-label={`Nominate now with the ${pkg.name} package`}
                >
                  Nominate Now
                  <ArrowRight size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================= */}
      {/* WINNERS — no confirmed finalists/winners yet, so this shows a  */}
      {/* generic "Coming Soon" placeholder instead of any names/logos.  */}
      {/* Swap this block for a real carousel once winners are finalized.*/}
      {/* ============================================================= */}
      <section className="bg-[#0B2447] text-white" id="winners">
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <Eyebrow>HONORING EXCELLENCE</Eyebrow>
          <h2 className="font-serif text-3xl sm:text-4xl mb-6">
            Celebrating Our Selected Winners
          </h2>
          <div className="mx-auto h-16 w-16 rounded-full border border-white/25 flex items-center justify-center mb-6">
            <Trophy size={26} className="text-[#F4801F]" />
          </div>
          <p className="text-lg font-semibold text-[#F4801F] mb-3">
            Coming Soon — Finalists to be Announced
          </p>
          <p className="text-white/70 leading-relaxed">
            Nominations for the Freedom Excellence Awards 2026 are still open.
            Winners will be selected by our jury and felicitated live at
            Freedom Conclave 2026 — check back here once finalists are
            confirmed.
          </p>
        </div>
      </section>

      {/* ============================================================= */}
      {/* CLOSING CTA                                                    */}
      {/* ============================================================= */}
      <section className="px-6 py-14">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6 bg-white rounded-2xl shadow-sm ring-1 ring-slate-100 px-8 py-8">
          <div className="flex items-center gap-5 text-center sm:text-left">
            <span className="hidden sm:flex h-14 w-14 rounded-full border-2 border-[#0B2447] items-center justify-center shrink-0">
              <Trophy size={22} className="text-[#0B2447]" />
            </span>
            <div>
              <p className="text-xs font-semibold tracking-[0.2em] text-[#F4801F] mb-1">
                BE RECOGNIZED. BE CELEBRATED. BE AN INSPIRATION.
              </p>
              <h3 className="font-serif text-2xl text-[#0B2447]">
                Nominate Now &amp; Make a Difference
              </h3>
            </div>
          </div>
          <button
            onClick={() => setNominationOpen(true)}
            className="inline-flex items-center gap-2 bg-[#0B2447] text-white font-semibold px-7 py-3 rounded-full hover:bg-[#123a6b] transition-colors whitespace-nowrap"
            aria-label="Nominate now and make a difference"
          >
            Nominate Now
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* ============================================================= */}
      {/* NOMINATION MODAL — shared by every "Nominate Now" trigger      */}
      {/* ============================================================= */}
      <NominationModal
        open={nominationOpen}
        onClose={() => setNominationOpen(false)}
        categories={CATEGORIES}
      />

      {/* ============================================================= */}
      {/* FOOTER — exact same design as MegaEvent's footer, but kept as  */}
      {/* its own separate, independent copy (no shared import/routing) */}
      {/* ============================================================= */}
      <footer id="contact" className="bg-ibh-navy text-white relative overflow-hidden" data-testid="footer">
        <div className="absolute inset-0 grain mix-blend-overlay"></div>
        <div className="absolute -top-32 right-0 h-72 w-72 rounded-full bg-ibh-green/15 blur-3xl"></div>
        <div className="section relative pt-20 pb-10">
          <div className="grid lg:grid-cols-12 gap-x-20 gap-y-12">
            <div className="lg:col-span-4">
             <Wordmark light context="awards footer" />
              <p className="mt-5 text-white/65 text-sm max-w-sm leading-relaxed">
                India Business Helpline (IBH) is the all-in-one ecosystem helping startups, MSMEs and entrepreneurs navigate every stage of business — with expert guidance and a powerful community.
              </p>
              <div className="mt-6 flex items-center gap-3">
                {awardsSocials.map((s, i) => (<AwardsFooterSocialIcon key={i} {...s} />))}
              </div>
            </div>

            {awardsLinkGroups.map((g) => (
              <div key={g.title} className="lg:col-span-2">
                <h4 className="font-display font-bold text-white text-sm uppercase tracking-wider">{g.title}</h4>
                <ul className="mt-5 space-y-3">
                  {g.links.map((l) => (
                    <li key={l.label}>
                      <a href={l.href} className="text-sm text-white/65 hover:text-white transition-colors" target={l.href.startsWith("http") ? "_blank" : undefined} rel={l.href.startsWith("http") ? "noopener noreferrer" : undefined}>
                        {l.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <div className="lg:col-span-2">
              <h4 className="font-display font-bold text-white text-sm uppercase tracking-wider">Contact US</h4>
              <ul className="mt-5 space-y-3 text-sm text-white/70">
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-envelope mt-1 text-ibh-green"></i>
                  <a href="mailto:indianbusinesshelpline@gmail.com" className="hover:text-white transition-colors">Click to E-Mail</a>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-phone mt-1 text-ibh-green"></i>
                  <a href="tel:+918100301388" className="hover:text-white transition-colors whitespace-nowrap">+91 81003 01388</a>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-phone mt-1 text-ibh-green"></i>
                  <a href="tel:+918274964884" className="hover:text-white transition-colors whitespace-nowrap">+91 82749 64884</a>
                </li>
                <li className="flex items-start gap-2">
                  <i className="fa-solid fa-location-dot mt-1 text-ibh-green"></i>
                  <a href="https://maps.app.goo.gl/rnJSMm8raQDGcMGi8" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Kolkata</a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-white/50">
            <p>© {new Date().getFullYear()} India Business Helpline. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}